import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { useLocales, useTheme } from '../../hooks';
import { Tab, Tabs } from '@mui/material';
import { PlanForm } from './Plans/PlanForm';
import { PromoForm } from './Promotions/PromoForm';
import { OfferMappingForm } from './OfferMappings/OfferMappingForm';
import { PlanPickerForm } from './PlanPickerManager/PlanPickerForm';
import { AppRoutes } from '../../Routes';
import { Link, Outlet } from 'react-router-dom';
import {
  Campaign,
  CloudSyncTwoTone,
  Handshake,
  MonetizationOn,
  Sell,
  ViewList,
  WorkspacePremium
} from '@mui/icons-material';
import { paymentsTestIds } from '../shared/TestsIds';
import { SponsorForm } from './Sponsors/SponsorForm';
import { CampaignForm } from './Campaigns/CampaignForm';

const tabHeight = 64;

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0, 4),
    display: 'flex',
    gap: theme.spacing(6),
    alignItems: 'center'
  },
  tab: {
    flexDirection: 'row',
    gap: theme.spacing(2),
    alignItems: 'center',
    minHeight: tabHeight,
    padding: theme.spacing(0, 5),
    '& > svg': {
      margin: '0 !important'
    }
  }
}));

export default function Payments(): JSX.Element {
  const { t } = useLocales();
  const { classes } = useStyles();
  const { formControlColor } = useTheme();

  return (
    <>
      <div className={classes.root}>
        <div className={classes.header}>
          <h2>{t('payments.monetization')}</h2>

          <Tabs
            data-testid={paymentsTestIds.tabs}
            value={location.pathname}
            indicatorColor={formControlColor}
            textColor={formControlColor}
          >
            <Tab
              className={classes.tab}
              data-testid={paymentsTestIds.tabPromotions}
              label={t('payments.promotions')}
              icon={<WorkspacePremium fontSize="medium" />}
              component={Link}
              to={AppRoutes.paymentsPromotions}
              value={AppRoutes.paymentsPromotions}
            />
            <Tab
              className={classes.tab}
              data-testid={paymentsTestIds.tabPlans}
              label={t('payments.plans')}
              icon={<MonetizationOn fontSize="medium" />}
              to={AppRoutes.paymentsPlans}
              value={AppRoutes.paymentsPlans}
              component={Link}
            />
            <Tab
              className={classes.tab}
              data-testid={paymentsTestIds.tabCoupons}
              label={t('payments.coupons')}
              icon={<Sell fontSize="medium" />}
              component={Link}
              to={AppRoutes.paymentsCoupons}
              value={AppRoutes.paymentsCoupons}
            />
            <Tab
              className={classes.tab}
              data-testid={paymentsTestIds.tabOfferMappings}
              label={t('payments.offer_mappings')}
              icon={<CloudSyncTwoTone fontSize="medium" />}
              component={Link}
              to={AppRoutes.paymentsOfferMappings}
              value={AppRoutes.paymentsOfferMappings}
            />
            <Tab
              className={classes.tab}
              data-testid={paymentsTestIds.tabPlanPickers}
              label={t('payments.plan_pickers')}
              icon={<ViewList fontSize="medium" />}
              component={Link}
              to={AppRoutes.paymentsPlanPickers}
              value={AppRoutes.paymentsPlanPickers}
            />
            <Tab
              className={classes.tab}
              data-testid={paymentsTestIds.tabSponsors}
              label={t('payments.sponsors')}
              icon={<Handshake fontSize="medium" />}
              component={Link}
              to={AppRoutes.paymentsSponsors}
              value={AppRoutes.paymentsSponsors}
            />
            <Tab
              className={classes.tab}
              data-testid={paymentsTestIds.tabCampaigns}
              label={t('payments.campaigns')}
              icon={<Campaign fontSize="medium" />}
              component={Link}
              to={AppRoutes.paymentsCampaigns}
              value={AppRoutes.paymentsCampaigns}
            />
            {/* Hiding this tab until we need it */}
            {/* <Tab
              className={classes.tab}
              data-testid={testIds.tabDefaults}
              label={t('payments.default_plan_groups')}
              icon={<Devices fontSize="medium" />}
              component={Link}
              to={AppRoutes.paymentsDefaults}
              value={AppRoutes.paymentsDefaults}
            /> */}
          </Tabs>
        </div>
        <Outlet />
      </div>
      {/* CT-4577: each tab loads the datasets it reads, so opening one no longer waits on the other
          six. These forms stay mounted at the section level, but they only read Recoil state, so the
          tab that opens them has already fetched what they need. */}
      <PlanForm />
      <PromoForm />
      <OfferMappingForm />
      <PlanPickerForm />
      <SponsorForm />
      <CampaignForm />
    </>
  );
}
